import { Box, Button, Icon, PlainButton, PlainLink, Text } from '@customerio/pluma-components/react';
import { useTrackEvent } from '@/features/track/hook';
import { useSafeNavigate } from '@/context/safe-navigate';
import * as styles from './index.css';
import { useExperiment } from '@/hooks/use-experiment';

interface RocketFooterProps {
  productTourHref?: ((workspaceId: number | null) => string) | null;
  videoHref?: string | null;
  workspaceId: number | null;
  shouldDisplayUpgradeButton?: boolean;
}

export function RocketFooter({
  productTourHref,
  videoHref,
  workspaceId,
  shouldDisplayUpgradeButton,
}: RocketFooterProps) {
  const track = useTrackEvent();
  const safeNavigate = useSafeNavigate();
  const experiment = useExperiment('universalRocket');
  const isUniversalRocketEnabled = experiment === 'treatment';

  const handleStartProductTourClick = (event: React.MouseEvent) => {
    track('Rocket: Clicked on the Start Product Tour link');
    event.preventDefault();
    safeNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  const handleWatchVideoClick = () => {
    track('Rocket: Clicked on the Watch Video link');
  };

  const handleUpgradeButtonClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Upgrade button');
    safeNavigate(event.currentTarget.getAttribute('href') ?? '');
  };

  const handleGetHelpClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Get help link');
    // Opens Zendesk messenger widget
    (window as any).zE?.('messenger', 'open');
  };

  const handleGiveFeedbackClick = (event: React.MouseEvent) => {
    event.preventDefault();
    track('Rocket: Clicked on the Give feedback link');
    safeNavigate('/?feedback=true');
  };

  const isProductTourEnabled = typeof productTourHref === 'function';

  const isFeedbackEnabled = isUniversalRocketEnabled && !isProductTourEnabled;

  return (
    <Box as="footer" p="250" className={styles.rocketContentFooter} display="flex" justifyContent="space-between">
      {shouldDisplayUpgradeButton === true ? (
        <Button variant="primary" href="/settings/billing/available-plans/journeys" onClick={handleUpgradeButtonClick}>
          Upgrade
        </Button>
      ) : (
        <>
          {isProductTourEnabled ? (
            <PlainLink
              href={productTourHref(workspaceId)}
              alignItems="center"
              display="flex"
              onClick={handleStartProductTourClick}
            >
              <Icon name="cursor" mr="050" color="accent" />
              <Text size="xs">{isUniversalRocketEnabled ? 'Product tour' : 'Start product tour'}</Text>
            </PlainLink>
          ) : null}
          {isUniversalRocketEnabled ? (
            <PlainLink href="https://docs.customer.io/videos/" isExternal={true} alignItems="center" display="flex">
              <Icon name="play" mr="050" color="accent" />
              <Text size="xs">Videos</Text>
            </PlainLink>
          ) : (
            <>
              {videoHref != null ? (
                <PlainLink
                  href={videoHref}
                  isExternal={true}
                  alignItems="center"
                  display="flex"
                  onClick={handleWatchVideoClick}
                >
                  <Icon name="play" mr="050" color="accent" />
                  <Text size="xs">Watch video</Text>
                </PlainLink>
              ) : null}
            </>
          )}
          {isUniversalRocketEnabled ? (
            <PlainButton alignItems="center" display="flex" onClick={handleGetHelpClick}>
              <Icon name="slack" mr="050" color="accent" />
              <Text size="xs">Get help</Text>
            </PlainButton>
          ) : null}
          {isFeedbackEnabled ? (
            <PlainButton alignItems="center" display="flex" onClick={handleGiveFeedbackClick}>
              <Icon name="heart" mr="050" color="accent" />
              <Text size="xs">Give feedback</Text>
            </PlainButton>
          ) : null}
        </>
      )}
    </Box>
  );
}
